import React from 'react';

/**
 * Generic Export Modal Component
 * Consolidates 12+ export modals into a single reusable component.
 * 
 * Usage:
 * import ExportModal from './common/ExportModal';
 * import { EXPORT_CONFIGS } from '../config/exportConfigs';
 * 
 * <ExportModal
 *   {...EXPORT_CONFIGS.tariff}
 *   isOpen={showExport}
 *   onClose={() => setShowExport(false)}
 *   onExport={handleExport}
 *   isExporting={isExporting}
 *   jobNumber={jobNumber}  // Optional
 * />
 */
const ExportModal = ({
  isOpen,
  onClose,
  onExport,
  isExporting,
  // Customization props
  title = 'Export Data',
  description = 'This will export all records to an Excel file.',
  icon = '📊',
  accentColor = '#17a2b8',
  infoBgColor = '#d1ecf1',
  infoTextColor = '#0c5460',
  // Optional
  jobNumber,
  exportStatus,
  statusType, // 'success' | 'error' | 'info'
  entityName, // For "Exporting X records for Job: Y" message
  exportButtonText = 'Export to Excel',
}) => {
  if (!isOpen) return null;

  // Determine status box colors
  const getStatusColors = () => {
    if (!exportStatus) return null;
    if (statusType === 'error' || exportStatus.includes('Error') || exportStatus.includes('failed')) {
      return { bg: '#f8d7da', text: '#721c24' };
    }
    if (statusType === 'success' || exportStatus.includes('complete')) {
      return { bg: '#d4edda', text: '#155724' };
    }
    return { bg: '#d1ecf1', text: '#0c5460' }; // info
  };

  const statusColors = getStatusColors();

  // Build the summary message
  const getSummaryMessage = () => {
    if (jobNumber && entityName) {
      return `Exporting ${entityName} records for Job: ${jobNumber}`;
    }
    if (jobNumber) {
      return `Exporting records for Job: ${jobNumber}`;
    }
    if (entityName) {
      return `All ${entityName} records will be exported`;
    }
    return 'All records will be exported';
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            {icon} {title}
          </h3>
          <button
            onClick={onClose}
            disabled={isExporting}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: isExporting ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        {/* Description Box */}
        <div style={{
          backgroundColor: infoBgColor,
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
          color: infoTextColor
        }}>
          <strong>Export Details:</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            {description}
          </p>
        </div>

        {/* Status Box (for job queue exports) */}
        {exportStatus && statusColors && (
          <div style={{
            backgroundColor: statusColors.bg,
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            color: statusColors.text
          }}>
            {exportStatus}
          </div>
        )}

        {/* Summary Box */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#495057' }}>
            <span style={{ fontSize: '1.2rem' }}>{icon}</span>
            <span>{getSummaryMessage()}</span>
          </div>
        </div>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={isExporting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              opacity: isExporting ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={onExport}
            disabled={isExporting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: isExporting ? '#6c757d' : accentColor,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {isExporting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #fff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Exporting...
              </>
            ) : (
              <>📊 {exportButtonText}</>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ExportModal;

